import React, { useState, useMemo, useEffect } from 'react';
import apiClient from '../api/apiClient';
import Swal from 'sweetalert2';
import Modal from 'react-modal';
import Cropper from 'react-easy-crop';
import { getCroppedImg } from '../utils/cropUtils';
import { Document, Page } from 'react-pdf';
import { pdfjs } from 'react-pdf';
import { getUserId } from '../utils/auth';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

// Set modal root for accessibility
Modal.setAppElement('#root');

const DataTable = ({ 
  data: initialData, 
  endpoint, 
  onDataChange, 
  currentTab 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState(initialData || []);
  const [isAdding, setIsAdding] = useState(false);
  const [newItem, setNewItem] = useState({});
  const [foreignKeyData, setForeignKeyData] = useState({});
  const [foreignKeyLoading, setForeignKeyLoading] = useState(false);
  const [foreignKeyErrors, setForeignKeyErrors] = useState({});
  const [displayColumns, setDisplayColumns] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);

  // Capture Article Modal State
  const [isCaptureModalOpen, setIsCaptureModalOpen] = useState(false);
  const [currentMatch, setCurrentMatch] = useState(null);
  const [pdfUrl, setPdfUrl] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [numPages, setNumPages] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [pdfImageUrl, setPdfImageUrl] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const pageSizeOptions = [10, 30, 50, { value: 'all', label: 'All' }];

  // List of boolean columns that need Yes/No display
  const booleanColumns = ['is_active', 'is_test', 'is_processed', 'is_deleted'];
  
  // List of read-only columns (timestamps)
  const readOnlyColumns = ['id', 'file_uuid', 'created_at', 'updated_at', 'token', 'reset_password'];

  // Memoize the hiddenColumns array so it doesn't change between renders
  const hiddenColumns = useMemo(() => [
    'id', 
    'file_uuid', 
    'token', 
    'reset_password',
    'file_encoded'
  ], []);

  // Weekday options for day_of_week column
  const weekdays = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY'];

  // Convert boolean value to Yes/No for display
  const displayBooleanValue = (value) => {
    if (value === true || value === 1 || value === '1') return 'Yes';
    if (value === false || value === 0 || value === '0') return 'No';
    return value;
  };

  // Convert Yes/No back to 1/0 for submission
  const parseBooleanInput = (value) => {
    if (value === 'Yes') return 1;
    if (value === 'No') return 0;
    return value;
  };

  // Get the name for a foreign key value
  const getForeignKeyName = (column, id) => {
    if (!foreignKeyData[column] || !id) return id;
    const relatedItem = foreignKeyData[column].find(item => item.id === id);
    return relatedItem ? (relatedItem.name || relatedItem.title || relatedItem.label || id) : id;
  };

  const renderPdfPageToImage = async (url, pageNum) => {
    try {
      setIsPdfLoading(true);
      const loadingTask = pdfjs.getDocument(url);
      const pdf = await loadingTask.promise;
      const page = await pdf.getPage(pageNum);
      
      const viewport = page.getViewport({ scale: 1.5 });
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      
      await page.render({
        canvasContext: context,
        viewport: viewport
      }).promise;
      
      const imageUrl = canvas.toDataURL('image/jpeg');
      setPdfImageUrl(imageUrl);
      setNumPages(pdf.numPages);
    } catch (error) {
      console.error('Error rendering PDF:', error);
      Swal.fire({
        title: 'Error',
        text: 'Failed to load PDF page',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    } finally {
      setIsPdfLoading(false);
    }
  };
  // const renderPdfPageToImage = async (url, pageNum) => {
  //   try {
  //     setIsPdfLoading(true);
  //     const loadingTask = pdfjs.getDocument(url);
  //     const pdf = await loadingTask.promise;
  //     const page = await pdf.getPage(pageNum);
      
  //     const viewport = page.getViewport({ scale: 1.5 });
  //     const canvas = document.createElement('canvas');
  //     const context = canvas.getContext('2d');
  //     canvas.height = viewport.height;
  //     canvas.width = viewport.width;
      
  //     await page.render({
  //       canvasContext: context,
  //       viewport: viewport
  //     }).promise;
      
  //     const imageUrl = canvas.toDataURL('image/jpeg');
  //     setPdfImageUrl(imageUrl);
  //     setNumPages(pdf.numPages);
  //   } catch (error) {
  //     console.error('Error rendering PDF:', error);
  //     Swal.fire({
  //       title: 'Error',
  //       text: 'Failed to load PDF page',
  //       icon: 'error',
  //       confirmButtonText: 'OK'
  //     });
  //   } finally {
  //     setIsPdfLoading(false);
  //   }
  // };

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setSelectedFile(file);
    
    // Auto-populate name field with filename (without extension)
    if (file && isAdding) {
      setNewItem(prev => ({
        ...prev,
        name: file.name.replace(/\.[^/.]+$/, "")
      }));
    }
  };

  // Sync with parent data changes
  useEffect(() => {
    setData(initialData || []);
    setCurrentPage(1);
  }, [initialData]);

  // Set display columns (excluding hidden columns)
  useEffect(() => {
    if (data.length > 0) {
      const allColumns = Object.keys(data[0]);
      setDisplayColumns(allColumns.filter(col => !hiddenColumns.includes(col)));
    }
  }, [data, hiddenColumns]);

  // Identify foreign key columns and fetch their data
  useEffect(() => {
    if (data.length === 0) return;

    const fkColumns = Object.keys(data[0])
      .filter(col => col.endsWith('_id') && col !== 'id')
      .filter(col => !(col in foreignKeyData));

    if (fkColumns.length === 0) return;

    const fetchForeignKeyData = async () => {
      setForeignKeyLoading(true);
      const newFkData = {};
      const newErrors = {};
      
      for (const col of fkColumns) {
        let relatedEndpoint = col.replace(/_id$/, '');
        
        if (relatedEndpoint === 'address') {
          relatedEndpoint = 'addresses';
        } else if (relatedEndpoint.endsWith('y')) {
          relatedEndpoint = relatedEndpoint.slice(0, -1) + 'ies';
        } else {
          relatedEndpoint = relatedEndpoint + 's';
        }

        try {
          const response = await apiClient.get(`api/${relatedEndpoint}/v1`);
          newFkData[col] = response.data;
        } catch (error) {
          console.error(`Error fetching data for ${relatedEndpoint}:`, error);
          newErrors[col] = `Could not load ${relatedEndpoint}`;
          newFkData[col] = [];
        }
      }
      
      setForeignKeyData(prev => ({ ...prev, ...newFkData }));
      setForeignKeyErrors(prev => ({ ...prev, ...newErrors }));
      setForeignKeyLoading(false);
    };
    
    fetchForeignKeyData();
  }, [data, foreignKeyData]);

  // Handle capture article action
  const handleCaptureArticle = async (match) => {
    setCurrentMatch(match);
    setIsCaptureModalOpen(true);
    
    try {
      // Fetch the article details
      const articleResponse = await apiClient.get(`api/articles/v1/${match.article_id}`);
      const article = articleResponse.data;
      setPageNumber(article.page_number || 1);
      
      // Fetch the PDF file details
      const pdfResponse = await apiClient.get(`api/pdffiles/v1/${article.pdffile_id}`);
      const pdfFile = pdfResponse.data;
      
      // Construct proper PDF URL - adjust based on your API
      const pdfUrl = `/api/pdffiles/v1/file/${pdfFile.id}`;
      setPdfUrl(pdfUrl);
      
      // Render initial page
      await renderPdfPageToImage(pdfUrl, article.page_number || 1);
      
    } catch (error) {
      console.error('Error fetching article or PDF data:', error);
      Swal.fire({
        title: 'Error',
        text: 'Could not load article data',
        icon: 'error',
        confirmButtonText: 'OK'
      });
      setIsCaptureModalOpen(false);
    }
  };

  // Handle PDF document load
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  // Handle crop completion
  const onCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  // Handle the actual capture
  const handleCapture = async () => {
    if (!croppedAreaPixels || !currentMatch || !pdfImageUrl) return;
    
    setIsCapturing(true);
    
    try {
      // Get the cropped image
      const croppedImage = await getCroppedImg(
        pdfImageUrl,
        croppedAreaPixels,
      );
      
      // Post the captured image to the API
      const userId = getUserId();

      const response = await apiClient.post('api/capture-article-match/v1', {
        user_id: userId,
        match_id: currentMatch.id,
        page_number: pageNumber,
        image_base64: croppedImage
      });
      
      if (response.data.success) {
        await Swal.fire({
          title: 'Success',
          text: 'Article captured successfully',
          icon: 'success',
          confirmButtonText: 'OK'
        });
        
        // Refresh the matches data
        const refreshResponse = await apiClient.get(`api/${endpoint}/v1`);
        setData(refreshResponse.data);
        onDataChange?.();
        
        // Close the modal
        setIsCaptureModalOpen(false);
      }
    } catch (error) {
      console.error('Error capturing article:', error);
      Swal.fire({
        title: 'Error',
        text: 'Failed to capture article',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    } finally {
      setIsCapturing(false);
    }
  };

  const filteredData = data.filter((item) =>
    Object.entries(item).some(([key, value]) => {
      if (key.endsWith('_id') && foreignKeyData[key]) {
        const name = getForeignKeyName(key, value);
        return name.toLowerCase().includes(searchTerm.toLowerCase());
      }
      return (
        value &&
        value.toString().toLowerCase().includes(searchTerm.toLowerCase())
      );
    })
  );

  // Pagination logic
  const indexOfLastItem = itemsPerPage === 'all' ? filteredData.length : currentPage * itemsPerPage;
  const indexOfFirstItem = itemsPerPage === 'all' ? 0 : (currentPage - 1) * itemsPerPage;
  const currentItems = itemsPerPage === 'all' 
    ? filteredData 
    : filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = itemsPerPage === 'all' ? 1 : Math.ceil(filteredData.length / itemsPerPage);

  const handlePageChange = async (newPage) => {
    const page = Math.max(1, Math.min(numPages, newPage));
    setPageNumber(page);
    await renderPdfPageToImage(pdfUrl, page);
  };

  const handleItemsPerPageChange = (e) => {
    const value = e.target.value === 'all' ? 'all' : parseInt(e.target.value);
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    const editData = { ...item };
    booleanColumns.forEach(col => {
      if (col in editData) {
        editData[col] = displayBooleanValue(editData[col]);
      }
    });
    setFormData(editData);
  };

  const handleCancel = () => {
    setEditingId(null);
    setIsAdding(false);
    setSelectedFile(null);
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      let dataToSend;
      let response;
      
      if (editingId) {
        dataToSend = { ...formData };
        booleanColumns.forEach(col => {
          if (col in dataToSend) {
            dataToSend[col] = parseBooleanInput(dataToSend[col]);
          }
        });
        
        response = await apiClient.put(`api/${endpoint}/v1/${editingId}`, dataToSend);
        setData(data.map(item => item.id === editingId ? response.data : item));
        onDataChange?.();
      } else if (isAdding) {
        dataToSend = { ...newItem };
        booleanColumns.forEach(col => {
          if (col in dataToSend) {
            dataToSend[col] = parseBooleanInput(dataToSend[col]);
          }
        });
        
        readOnlyColumns.forEach(col => {
          delete dataToSend[col];
        });
        
        if (currentTab === 'pdffiles' && selectedFile) {
          const formData = new FormData();
          
          // Validate file type
          if (!selectedFile.type.includes('pdf')) {
            await Swal.fire({
              title: 'Invalid File',
              text: 'Please upload a PDF file',
              icon: 'error',
              confirmButtonText: 'OK'
            });
            return;
          }
          
          // Validate file size (e.g., 5MB limit)
          if (selectedFile.size > 50 * 1024 * 1024) {
            await Swal.fire({
              title: 'File Too Large',
              text: 'Maximum file size is 50MB',
              icon: 'error',
              confirmButtonText: 'OK'
            });
            return;
          }
          
          // 1. Append the file first
          formData.append('pdf_file', selectedFile);
          
          // 2. Then append other data
          Object.keys(dataToSend).forEach(key => {
            // Skip file_encoded if it exists since we're sending the actual file
            if (key !== 'file_encoded') {
              formData.append(key, dataToSend[key]);
            }
          });
          
          // Debugging: Log FormData contents
          console.log('FormData contents:');
          for (let [key, value] of formData.entries()) {
            console.log(key, value);
          }
          
          response = await apiClient.post(`api/${endpoint}/v1`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            },
            transformRequest: (data) => data, // Prevent axios from transforming the data
          });
        } else {
          response = await apiClient.post(`api/${endpoint}/v1`, dataToSend);
        }
        
        setData([...data, response.data]);
        onDataChange?.();
      }
  
      if (response?.data?.message) {
        await Swal.fire({
          title: 'Success',
          text: response.data.message,
          icon: 'success',
          confirmButtonText: 'OK'
        });
        
        try {
          const refreshResponse = await apiClient.get(`api/${endpoint}/v1`);
          setData(refreshResponse.data);
          onDataChange?.();
        } catch (refreshError) {
          console.error('Error refreshing data:', refreshError);
        }
      }
    } catch (error) {
      console.error('Error saving data:', error);
      
      // Enhanced error logging for file uploads
      if (currentTab === 'pdffiles' && selectedFile) {
        console.error('File upload error details:', {
          fileName: selectedFile.name,
          fileSize: selectedFile.size,
          fileType: selectedFile.type
        });
      }
      
      if (error.response?.data?.message) {
        await Swal.fire({
          title: 'Error',
          text: error.response.data.message,
          icon: 'error',
          confirmButtonText: 'OK'
        });
      } else {
        await Swal.fire({
          title: 'Error',
          text: 'An error occurred while saving the data',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      }
    } finally {
      setIsLoading(false);
      setEditingId(null);
      setIsAdding(false);
      setSelectedFile(null);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      setIsLoading(true);
      try {
        const response = await apiClient.delete(`api/${endpoint}/v1/${id}`);
        setData(data.filter(item => item.id !== id));
        onDataChange?.();

        if (response?.data?.message) {
          await Swal.fire({
            title: 'Success',
            text: response.data.message,
            icon: 'success',
            confirmButtonText: 'OK'
          });

          try {
            const refreshResponse = await apiClient.get(`api/${endpoint}/v1`);
            setData(refreshResponse.data);
            onDataChange?.();
          } catch (refreshError) {
            console.error('Error refreshing data:', refreshError);
          }
        }
      } catch (error) {
        console.error('Error deleting item:', error);
        if (error.response?.data?.message) {
          await Swal.fire({
            title: 'Error',
            text: error.response.data.message,
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (editingId) {
      setFormData({ ...formData, [name]: value });
    } else if (isAdding) {
      setNewItem({ ...newItem, [name]: value });
    }
  };

  const handleAddNew = () => {
    setIsAdding(true);
    if (data.length > 0) {
      const emptyItem = {};
      Object.keys(data[0]).forEach(key => {
        emptyItem[key] = booleanColumns.includes(key) ? 'No' : '';
        if (readOnlyColumns.includes(key)) {
          delete emptyItem[key];
        }
      });
      setNewItem(emptyItem);
    }
    setSelectedFile(null);
  };

  if (!data || data.length === 0) {
    return (
      <div className="data-table">
        <button className="btn-add" onClick={handleAddNew}>
          Add New
        </button>
        <div>No data available</div>
      </div>
    );
  }

  const allColumns = Object.keys(data[0]).filter(col => 
    !(isAdding && readOnlyColumns.includes(col)) &&
    !hiddenColumns.includes(col)
  );

  return (
    <div className="data-table">
      <div className="search-bar">
        <input
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <button className="btn-add" onClick={handleAddNew}>
          Add New
        </button>
      </div>
      
      <div className="pagination-controls">
        <div className="items-per-page">
          <label>Items per page:</label>
          <select 
            value={itemsPerPage} 
            onChange={handleItemsPerPageChange}
            disabled={isLoading}
          >
            {pageSizeOptions.map(option => (
              <option key={option.value || option} value={option.value || option}>
                {option.label || option}
              </option>
            ))}
          </select>
        </div>
        
        {itemsPerPage !== 'all' && (
          <div className="pagination-navigation">
            <button 
              onClick={() => handlePageChange(1)} 
              disabled={currentPage === 1 || isLoading}
            >
              First
            </button>
            <button 
              onClick={() => handlePageChange(currentPage - 1)} 
              disabled={currentPage === 1 || isLoading}
            >
              Previous
            </button>
            
            <span className="page-info">
              Page {currentPage} of {totalPages}
            </span>
            
            <button 
              onClick={() => handlePageChange(currentPage + 1)} 
              disabled={currentPage === totalPages || isLoading}
            >
              Next
            </button>
            <button 
              onClick={() => handlePageChange(totalPages)} 
              disabled={currentPage === totalPages || isLoading}
            >
              Last
            </button>
          </div>
        )}
      </div>
      
      {isAdding && (
        <div className="add-form">
          <h3>Add New Item</h3>
          <div className="form-fields">
            {/* Only show file upload when currentTab is 'pdffiles' */}
            {currentTab === 'pdffiles' && (
              <div className="form-field">
                <label>Upload PDF:</label>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileChange}
                />
              </div>
            )}
            
            {allColumns.map((col) => (
              <div key={`new-${col}`} className="form-field">
                <label>{col.replace(/_/g, ' ')}:</label>
                {booleanColumns.includes(col) ? (
                  <select
                    name={col}
                    value={newItem[col] || 'No'}
                    onChange={handleChange}
                  >
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                ) : col === 'day_of_week' ? (
                  <select
                    name={col}
                    value={newItem[col] || ''}
                    onChange={handleChange}
                  >
                    <option value="">Select Day</option>
                    {weekdays.map(day => (
                      <option key={day} value={day}>{day}</option>
                    ))}
                  </select>
                ) : col.endsWith('_id') && foreignKeyData[col] ? (
                  <>
                    {foreignKeyErrors[col] ? (
                      <div className="error-message">{foreignKeyErrors[col]}</div>
                    ) : (
                      <select
                        name={col}
                        value={newItem[col] || ''}
                        onChange={handleChange}
                        disabled={foreignKeyLoading}
                      >
                        <option value="">Select {col.replace('_id', '').replace(/_/g, ' ')}</option>
                        {foreignKeyData[col].map(item => (
                          <option key={item.id} value={item.id}>
                            {item.name || item.title || item.label || item.id}
                          </option>
                        ))}
                      </select>
                    )}
                  </>
                ) : (
                  <input
                    type="text"
                    name={col}
                    value={newItem[col] || ''}
                    onChange={handleChange}
                    disabled={col === 'id' || readOnlyColumns.includes(col)}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="form-actions">
            <button onClick={handleSave} disabled={isLoading}>
              {isLoading ? 'Saving...' : 'Save'}
            </button>
            <button onClick={handleCancel} disabled={isLoading}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <table>
        <thead>
          <tr>
            {displayColumns.map((col) => (
              <th key={col}>
                {col.endsWith('_id') 
                  ? col.replace('_id', '').replace(/_/g, ' ')
                  : col.replace(/_/g, ' ')}
              </th>
            ))}
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {currentItems.map((item) => (
            <tr key={item.id}>
              {displayColumns.map((col) => (
                <td key={`${item.id}-${col}`}>
                  {editingId === item.id ? (
                    readOnlyColumns.includes(col) ? (
                      item[col]
                    ) : booleanColumns.includes(col) ? (
                      <select
                        name={col}
                        value={formData[col] || 'No'}
                        onChange={handleChange}
                      >
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </select>
                    ) : col === 'day_of_week' ? (
                      <select
                        name={col}
                        value={formData[col] || ''}
                        onChange={handleChange}
                      >
                        <option value="">Select Day</option>
                        {weekdays.map(day => (
                          <option key={day} value={day}>{day}</option>
                        ))}
                      </select>
                    ) : col.endsWith('_id') && foreignKeyData[col] ? (
                      <>
                        {foreignKeyErrors[col] ? (
                          <div className="error-message">{foreignKeyErrors[col]}</div>
                        ) : (
                          <select
                            name={col}
                            value={formData[col] || ''}
                            onChange={handleChange}
                            disabled={foreignKeyLoading}
                          >
                            <option value="">Select {col.replace('_id', '').replace(/_/g, ' ')}</option>
                            {foreignKeyData[col].map(fkItem => (
                              <option key={fkItem.id} value={fkItem.id}>
                                {fkItem.name || fkItem.title || fkItem.label || fkItem.id}
                              </option>
                            ))}
                          </select>
                        )}
                      </>
                    ) : (
                      <input
                        type="text"
                        name={col}
                        value={formData[col] || ''}
                        onChange={handleChange}
                        disabled={col === 'id' || readOnlyColumns.includes(col)}
                      />
                    )
                  ) : (
                    booleanColumns.includes(col) 
                      ? displayBooleanValue(item[col]) 
                      : col.endsWith('_id') 
                        ? getForeignKeyName(col, item[col]) 
                        : item[col]
                  )}
                </td>
              ))}
              <td>
                {editingId === item.id ? (
                  <>
                    <button onClick={handleSave} disabled={isLoading}>
                      {isLoading ? 'Saving...' : 'Save'}
                    </button>
                    <button onClick={handleCancel} disabled={isLoading}>
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button onClick={() => handleEdit(item)} disabled={isLoading || isAdding}>
                      Edit
                    </button>
                    <button 
                      onClick={() => handleDelete(item.id)} 
                      disabled={isLoading || isAdding}
                    >
                      Delete
                    </button>
                    {currentTab === 'matches' && item.match_state !== '2' && item.match_state !== '3' && (
                      <button 
                        onClick={() => handleCaptureArticle(item)}
                        disabled={isLoading || isAdding}
                      >
                        Capture Article
                      </button>
                    )}
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      
      {itemsPerPage !== 'all' && (
        <div className="pagination-controls bottom">
          <div className="pagination-navigation">
            <button 
              onClick={() => handlePageChange(1)} 
              disabled={currentPage === 1 || isLoading}
            >
              First
            </button>
            <button 
              onClick={() => handlePageChange(currentPage - 1)} 
              disabled={currentPage === 1 || isLoading}
            >
              Previous
            </button>
            
            <span className="page-info">
              Page {currentPage} of {totalPages} (Showing {currentItems.length} of {filteredData.length} items)
            </span>
            
            <button 
              onClick={() => handlePageChange(currentPage + 1)} 
              disabled={currentPage === totalPages || isLoading}
            >
              Next
            </button>
            <button 
              onClick={() => handlePageChange(totalPages)} 
              disabled={currentPage === totalPages || isLoading}
            >
              Last
            </button>
          </div>
        </div>
      )}

      {/* Capture Article Modal */}
      
      {/* Updated Capture Article Modal */}
      <Modal
        isOpen={isCaptureModalOpen}
        onRequestClose={() => setIsCaptureModalOpen(false)}
        contentLabel="Capture Article"
        className="modal"
        overlayClassName="overlay"
      >
        <h2>Capture Article</h2>
        
        <div className="pdf-capture-container">
          <div className="page-controls">
            <button 
              onClick={() => handlePageChange(pageNumber - 1)}
              disabled={pageNumber <= 1 || isPdfLoading}
            >
              Previous Page
            </button>
            <span>
              Page {pageNumber} of {numPages || '...'}
            </span>
            <button 
              onClick={() => handlePageChange(pageNumber + 1)}
              disabled={pageNumber >= (numPages || Infinity) || isPdfLoading}
            >
              Next Page
            </button>
          </div>

          {isPdfLoading ? (
            <div className="loading-message">Rendering PDF page...</div>
          ) : (
            pdfImageUrl && (
              <div className="crop-container">
                <Cropper
                  image={pdfImageUrl}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  onCropChange={setCrop}
                  onCropComplete={onCropComplete}
                  onZoomChange={setZoom}
                  cropShape="rect"
                  showGrid={true}
                  classes={{
                    containerClassName: "crop-container",
                    mediaClassName: "cropped-image",
                  }}
                />
              </div>
            )
          )}

          <div className="capture-controls">
            <div className="zoom-control">
              <label>Zoom:</label>
              <input
                type="range"
                value={zoom}
                min={1}
                max={3}
                step={0.1}
                onChange={(e) => setZoom(e.target.value)}
              />
            </div>
            
            <div className="action-buttons">
              <button 
                onClick={handleCapture} 
                disabled={isCapturing || !pdfImageUrl}
                className="capture-button"
              >
                {isCapturing ? 'Capturing...' : 'Capture Article'}
              </button>
              <button 
                onClick={() => setIsCaptureModalOpen(false)} 
                disabled={isCapturing}
                className="cancel-button"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default DataTable;